<template>
  <div>
    <VBusHelper
      :reactOn="$constants?.EVENTS?.RESTAURANT?.REVIEW_REQUIRED || 'restaurant.REVIEW_REQUIRED'"
      :handler="openReviewDialog"
    />
    <DialogComponent
      ref="dialog"
      type="modal"
      :dialogVisible="isOpen"
      @closed="onClosed"
      mainClass="fixed z-[65]"
    >
      <div class="p-2 sm:p-4">
        <!-- Header -->
        <div class="text-center mb-6">
          <div
            class="w-14 h-14 mx-auto mb-3 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500"
          >
            <svg class="w-8 h-8 fill-current" viewBox="0 0 20 20">
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
          </div>
          <h2 class="text-xl font-bold text-slate-800">
            Notez le restaurant
          </h2>
          <p class="text-xs text-slate-500 mt-1" v-if="restaurantInfoText">
            {{ restaurantInfoText }}
          </p>
        </div>

        <!-- Rating Stars -->
        <div class="flex flex-col items-center mb-6">
          <div class="flex items-center gap-2 sm:gap-3 mb-2">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              @click="setRating(star)"
              @mouseenter="hoverRating = star"
              @mouseleave="hoverRating = 0"
              class="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl transition-transform hover:scale-110 focus:outline-none"
              :class="
                (hoverRating || selectedRating) >= star
                  ? 'text-amber-400'
                  : 'text-slate-200'
              "
            >
              <svg
                class="w-8 h-8 fill-current drop-shadow-sm transition-colors duration-150"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </button>
          </div>
          <div class="h-5 text-sm font-semibold text-amber-600 transition-opacity">
            {{ ratingLabel }}
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <button
            type="button"
            @click="close"
            :disabled="isSubmitting"
            class="flex-1 py-3 px-4 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Plus tard
          </button>
          <button
            type="button"
            @click="submitReview"
            :disabled="isSubmitting"
            class="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-primary to-orange-500 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isSubmitting" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <span>{{ isSubmitting ? 'Envoi...' : 'Noter le restaurant' }}</span>
          </button>
        </div>
      </div>
    </DialogComponent>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const dialog = ref(null);
const isOpen = ref(false);
const restaurantData = ref(null);
const selectedRating = ref(0);
const hoverRating = ref(0);
const isSubmitting = ref(false);

const labels = {
  1: 'Très décevant',
  2: 'Décevant',
  3: 'Moyen',
  4: 'Très bien',
  5: 'Excellent !'
};

const ratingLabel = computed(() => {
  const current = hoverRating.value || selectedRating.value;
  return labels[current] || '';
});

const restaurantInfoText = computed(() => {
  if (!restaurantData.value) return '';
  return restaurantData.value.storeName || restaurantData.value.name || 'Restaurant';
});

function setRating(star) {
  selectedRating.value = star;
}

function openReviewDialog(data) {
  if (!data) return;
  restaurantData.value = data;
  selectedRating.value = 0;
  isSubmitting.value = false;
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  resetForm();
}

function onClosed() {
  isOpen.value = false;
  resetForm();
}

function resetForm() {
  selectedRating.value = 0;
  hoverRating.value = 0;
  isSubmitting.value = false;
}

function submitReview() {
  if (!selectedRating.value || selectedRating.value < 1 || selectedRating.value > 5) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Note requise',
      body: 'Veuillez attribuer une note entre 1 et 5 étoiles'
    });
    return;
  }

  const restaurantId = restaurantData.value?.id;

  if (!restaurantId) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Identifiant du restaurant introuvable'
    });
    return;
  }

  isSubmitting.value = true;
  bus.emit(constants.EVENTS.RESTAURANT.RATE, {
    restaurantId,
    rating: selectedRating.value
  });
}

function onRatedSuccess(res) {
  isSubmitting.value = false;
  isOpen.value = false;
  const id = restaurantData.value?.id;
  if (id) {
    // Optionally refresh restaurant details
    bus.emit(constants.EVENTS.RESTAURANT.RESTAURANT_DETAILS_REQUESTED, { id });
  }
  resetForm();
}

function onRateError() {
  isSubmitting.value = false;
}

onMounted(() => {
  bus.on(constants.EVENTS.RESTAURANT.REVIEW_REQUIRED, openReviewDialog);
  bus.on(constants.EVENTS.RESTAURANT.RATED, onRatedSuccess);
  bus.on(constants.EVENTS.RESTAURANT.RATE_ERROR, onRateError);
});

onBeforeUnmount(() => {
  bus.off(constants.EVENTS.RESTAURANT.REVIEW_REQUIRED, openReviewDialog);
  bus.off(constants.EVENTS.RESTAURANT.RATED, onRatedSuccess);
  bus.off(constants.EVENTS.RESTAURANT.RATE_ERROR, onRateError);
});

defineExpose({
  openReviewDialog,
  close
});
</script>
